"use client";

import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import * as React from "react";
import { renderAsChild } from "@/components/ui/slot";
import { cn } from "@/lib/utils";

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof BaseTooltip.Provider> & { delayDuration?: number }) {
  return <BaseTooltip.Provider data-slot="tooltip-provider" delay={delayDuration} {...props} />;
}

function Tooltip({ ...props }: React.ComponentProps<typeof BaseTooltip.Root>) {
  return (
    <TooltipProvider>
      <BaseTooltip.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

type TriggerProps = React.ComponentProps<typeof BaseTooltip.Trigger>;
type RenderProp = NonNullable<TriggerProps["render"]>;

function TooltipTrigger({
  asChild,
  children,
  ...props
}: React.ComponentProps<typeof BaseTooltip.Trigger> & { asChild?: boolean }) {
  return (
    <BaseTooltip.Trigger
      data-slot="tooltip-trigger"
      {...props}
      render={asChild ? (renderAsChild(children) as unknown as RenderProp) : (props.render as RenderProp | undefined)}
    >
      {asChild ? null : children}
    </BaseTooltip.Trigger>
  );
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof BaseTooltip.Popup> & {
  sideOffset?: number;
}) {
  // Floating UI's usePortalContext() resolves the portal container as:
  //   containerProp ?? parentPortalNode ?? document.body
  // When a tooltip is inside a Base UI Dialog, `parentPortalNode` is the dialog's portal node.
  // The dialog popup uses position:fixed + z-50, creating a stacking context that traps the
  // tooltip's position:absolute positioner — z-100 on the popup has no effect.
  //
  // Fix: force container=document.body so the tooltip is always in the root stacking context.
  // Use state (not `typeof document`) to avoid SSR/hydration mismatches — container starts as
  // null (portal renders nothing, consistent between server and client), then resolves to body
  // after mount. Tooltips are hover-only so they don't need to be available before hydration.
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    setPortalContainer(document.body);
  }, []);

  return (
    <BaseTooltip.Portal container={portalContainer}>
      {/* z-100 must be on the Positioner (position:absolute), not just the Popup (position:static).
          z-index only creates a stacking context on positioned elements. Without it here the
          positioner is in paint category 6 (z:auto positioned), always below positive z-index
          elements like the dialog popup at z-50 (paint category 7). */}
      <BaseTooltip.Positioner sideOffset={sideOffset} className="z-100">
        <BaseTooltip.Popup
          data-slot="tooltip-content"
          className={cn(
            "bg-primary text-primary-foreground w-fit max-w-56 origin-(--transform-origin) rounded-md px-3 py-1.5 text-xs text-left text-balance",
            "transition-[transform,opacity] duration-150 data-starting-style:opacity-0 data-starting-style:scale-95 data-ending-style:opacity-0 data-ending-style:scale-95",
            className,
          )}
          {...props}
        >
          {children}
          <BaseTooltip.Arrow className="flex items-center justify-center data-[side=bottom]:top-0 data-[side=bottom]:-translate-y-1/2 data-[side=left]:right-0 data-[side=left]:translate-x-1/2 data-[side=left]:rotate-90 data-[side=right]:left-0 data-[side=right]:-translate-x-1/2 data-[side=right]:-rotate-90 data-[side=top]:bottom-0 data-[side=top]:translate-y-1/2 data-[side=top]:rotate-180">
            <div className="bg-primary fill-primary size-2.5 rotate-45 rounded-[2px]" />
          </BaseTooltip.Arrow>
        </BaseTooltip.Popup>
      </BaseTooltip.Positioner>
    </BaseTooltip.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
